// Trail dei breadcrumb: contestuale (segue la navigazione reale) con fallback
// semantico (gerarchia canonica) su refresh/deep-link/nuova scheda.
// Alimentato da router.afterEach in main.js -> action TRACK_NAVIGATION.

import { makeCrumb, buildSemanticTrail, isExcluded, getSemanticParent } from '@/router/breadcrumbRegistry'

const STORAGE_KEY = 'breadcrumb_trail'
const MAX_TRAIL_LENGTH = 20

function persist(state) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.trail))
	} catch (e) {
		// storage non disponibile (es. modalità privata): il trail resta solo in memoria
	}
}

function isValidCrumb(crumb) {
	return crumb
		&& typeof crumb === 'object'
		&& typeof crumb.path === 'string'
		&& typeof crumb.fullPath === 'string'
		&& typeof crumb.labelKey === 'string'
		&& typeof crumb.type === 'string'
		&& typeof crumb.groupKey === 'string'
}

// Cerca dall'ultimo verso il primo: sezione e index del tab principale possono
// avere lo stesso path e deve vincere il crumb più profondo
function lastIndexOfPath(trail, path) {
	for (let i = trail.length - 1; i >= 0; i--) {
		if (trail[i].path === path) return i
	}
	return -1
}

function restoreTrail() {
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY)
		if (!stored) return null
		const trail = JSON.parse(stored)
		// se il trail salvato è corrotto o di uno schema diverso si scarta tutto:
		// il chiamante ricade sul fallback semantico
		if (!Array.isArray(trail) || !trail.length || !trail.every(isValidCrumb)) return null
		return trail
	} catch (e) {
		return null
	}
}

const state = {
	trail: [],
	breadcrumbVisible: true,
}

const getters = {
	breadcrumbTrail: state => state.trail,

	breadcrumbParent: state => state.trail.length > 1 ? state.trail[state.trail.length - 2] : null,

	// visibile anche con un solo crumb (index di sezione): la barra è sempre presente
	// dentro le sezioni così i tab non cambiano posizione tra un tab e l'altro
	breadcrumbVisible: state => state.breadcrumbVisible && state.trail.length > 0,
}

const mutations = {
	setTrail: (state, trail) => {
		state.trail = trail
		persist(state)
	},
	pushCrumb: (state, crumb) => {
		// cap di sicurezza: si scartano i crumb più vecchi dopo la radice
		while (state.trail.length >= MAX_TRAIL_LENGTH) {
			state.trail.splice(1, 1)
		}
		state.trail.push(crumb)
		persist(state)
	},
	replaceLastCrumb: (state, crumb) => {
		state.trail.splice(state.trail.length - 1, 1, crumb)
		persist(state)
	},
	truncateTrailAt: (state, { index, fullPath }) => {
		state.trail = state.trail.slice(0, index + 1)
		if (fullPath) {
			state.trail.splice(index, 1, { ...state.trail[index], fullPath })
		}
		persist(state)
	},
	setCrumbEntityLabel: (state, { groupKey, label }) => {
		if (!groupKey || !label) return
		state.trail = state.trail.map(crumb =>
			crumb.type === 'show' && crumb.groupKey === groupKey && crumb.entityLabel !== label
				? { ...crumb, entityLabel: label }
				: crumb
		)
		persist(state)
	},
	setBreadcrumbVisible: (state, visible) => {
		state.breadcrumbVisible = visible
	},
	clearTrail: state => {
		state.trail = []
		persist(state)
	},
}

const actions = {
	TRACK_NAVIGATION: ({ state, commit }, { to, from, router }) => {
		if (isExcluded(to)) {
			// route fuori scope (portale, calendario, login...): il trail resta intatto,
			// il componente si nasconde
			commit('setBreadcrumbVisible', false)
			return
		}
		commit('setBreadcrumbVisible', true)

		const crumb = makeCrumb(to)

		// la radice '/' (es. redirect post-404) equivale alla Dashboard: trail vuoto
		if (crumb.path === '/') {
			commit('clearTrail')
			return
		}

		const isBoot = !from || from.matched.length === 0

		if (isBoot || state.trail.length === 0) {
			const restored = restoreTrail()
			if (restored) {
				const index = lastIndexOfPath(restored, crumb.path)
				if (index >= 0) {
					// refresh (o duplica scheda) su una pagina già nel trail salvato
					const sliced = restored.slice(0, index + 1)
					sliced.splice(index, 1, { ...sliced[index], fullPath: crumb.fullPath })
					commit('setTrail', sliced)
					return
				}
			}
			// deep-link / nuova scheda / trail salvato incoerente
			commit('setTrail', buildSemanticTrail(to, router))
			return
		}

		// Dedup dei cicli: tornare su una pagina già nel trail tronca il trail a quel punto.
		// Copre click su crumb, browser back, $router.go(-1), backToList, hardware back.
		const index = lastIndexOfPath(state.trail, crumb.path)
		if (index >= 0) {
			commit('truncateTrailAt', { index, fullPath: crumb.fullPath })
			return
		}

		// Radice di modulo (voce di menu, index, pagina settings): il trail contestuale riparte
		if (crumb.type === 'index') {
			commit('setTrail', buildSemanticTrail(to, router))
			return
		}

		const last = state.trail[state.trail.length - 1]

		// Tab di un'altra scheda raggiunto direttamente (link a un tab, URL cambiato a mano, es. dalla commessa di un
		// progetto a quella di un altro): il tab va sotto la sua show, altrimenti resta appeso alla scheda precedente
		// e il breadcrumb ne mostra il nome. Show gia' nel trail: si torna li'; stessa scheda di un record fratello
		// (show + tab dello stesso tipo in coda): si sostituiscono, come il prev/next; altrimenti si accoda la show
		if (crumb.type === 'tab' && !(last && last.groupKey === crumb.groupKey)) {
			const showIndex = state.trail.map(item => item.type === 'show' && item.groupKey === crumb.groupKey).lastIndexOf(true)
			if (showIndex >= 0) {
				commit('truncateTrailAt', { index: showIndex })
				commit('pushCrumb', crumb)
				return
			}
			const parent = getSemanticParent(to, router)
			const show = parent ? makeCrumb(parent) : null
			if (show && show.type === 'show' && show.groupKey === crumb.groupKey) {
				const previous = state.trail[state.trail.length - 2]
				if (last && last.type === 'tab' && previous && previous.type === 'show' && previous.name === show.name) {
					commit('setTrail', state.trail.slice(0, state.trail.length - 2))
				}
				commit('pushCrumb', show)
			}
			commit('pushCrumb', crumb)
			return
		}

		// Tab dentro la stessa show: sostituzione, non accodamento
		if (last && last.groupKey === crumb.groupKey && crumb.type === 'tab') {
			if (last.type === 'tab') {
				commit('replaceLastCrumb', crumb)
			} else {
				commit('pushCrumb', crumb)
			}
			return
		}

		// Prev/next tra record fratelli (EntityNavigation): sostituzione
		if (last && crumb.type === 'show' && last.type === 'show' && crumb.name && crumb.name === last.name) {
			commit('replaceLastCrumb', crumb)
			return
		}

		// Le pagine terminali (edit/create/azioni) non si accumulano tra loro
		const terminalTypes = ['edit', 'create', 'action']
		if (last && terminalTypes.includes(last.type) && terminalTypes.includes(crumb.type)) {
			commit('replaceLastCrumb', crumb)
			return
		}

		// index -> show, show -> show, -> edit, -> create, azioni: accodamento
		commit('pushCrumb', crumb)
	},
}

export default {
	state,
	getters,
	mutations,
	actions,
}
